import { useState, useMemo, useCallback, useEffect } from 'react';
import { holidayApi, GROUP_OPTIONS, HOLIDAY_TYPE_OPTIONS, YEAR_OPTIONS } from '../api/holidayApi';
import { appToast } from '../../../../components/AppToast';

export function useHolidayList() {
  const [groupFilter, setGroupFilter] = useState('All');
  const [typeFilter, setTypeFilter] = useState('All');
  const [yearFilter, setYearFilter] = useState('2026');
  const [searchField, setSearchField] = useState('Holiday Name');
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [records, setRecords] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  const statusOptions = [
    { value: 'All', label: 'All Holidays' },
    { value: 'Upcoming', label: 'Upcoming' },
    { value: 'Observed', label: 'Past / Observed' },
  ];

  const searchFieldOptions = [
    { value: 'Holiday Name', label: 'Holiday Name' },
    { value: 'Description', label: 'Description' },
    { value: 'Day', label: 'Day of Week' },
  ];

  const tools = [
    { key: 'pdf', label: 'Export Holiday Calendar to PDF', icon: 'picture_as_pdf' },
    { key: 'excel', label: 'Export Holiday List to Excel', icon: 'table_view' },
    { key: 'print', label: 'Print Holiday Calendar', icon: 'print' },
  ];

  const loadData = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await holidayApi.getList();
      setRecords(res.data || []);
    } catch {
      // Fallback
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Filtered records
  const filteredRecords = useMemo(() => {
    return records.filter((item) => {
      // Status filter
      if (statusFilter !== 'All' && item.status !== statusFilter) {
        return false;
      }

      // Group filter
      if (groupFilter !== 'All' && item.group !== 'All Groups') {
        if (!item.groups || !item.groups.includes(groupFilter)) {
          return false;
        }
      }

      // Type filter
      if (typeFilter !== 'All' && item.type !== typeFilter) {
        return false;
      }

      // Year filter
      if (yearFilter !== 'All' && !item.date.startsWith(yearFilter)) {
        return false;
      }

      // Text search
      if (searchText.trim()) {
        const query = searchText.trim().toLowerCase();
        if (searchField === 'Holiday Name' && !item.name?.toLowerCase().includes(query)) return false;
        if (searchField === 'Description' && !item.description?.toLowerCase().includes(query)) return false;
        if (searchField === 'Day' && !item.day?.toLowerCase().includes(query)) return false;
      }

      return true;
    });
  }, [records, statusFilter, groupFilter, typeFilter, yearFilter, searchText, searchField]);

  // Summary statistics
  const summary = useMemo(() => {
    const total = filteredRecords.length;
    const upcoming = filteredRecords.filter((r) => r.status === 'Upcoming').length;
    const national = filteredRecords.filter((r) => r.type === 'National Holiday').length;
    const publicHolidays = filteredRecords.filter((r) => r.type === 'Public Holiday').length;
    const restricted = filteredRecords.filter((r) => r.type === 'Restricted Holiday').length;

    return {
      total,
      upcoming,
      national,
      publicHolidays,
      restricted,
    };
  }, [filteredRecords]);

  // Pagination
  const totalCount = filteredRecords.length;
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const paginatedRecords = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredRecords.slice(start, start + pageSize);
  }, [filteredRecords, page, pageSize]);

  // Actions
  const handleSearch = useCallback(() => {
    setPage(1);
    appToast.success('Holiday list filtered');
  }, []);

  const resetFilters = useCallback(() => {
    setGroupFilter('All');
    setTypeFilter('All');
    setYearFilter('2026');
    setStatusFilter('All');
    setSearchText('');
    setSearchField('Holiday Name');
    setPage(1);
  }, []);

  const handleToolSelect = useCallback((toolKey) => {
    const key = String(toolKey || '').toLowerCase();
    if (key.includes('print')) {
      window.print();
    } else if (key.includes('pdf')) {
      appToast.success('Exporting Holiday Calendar to PDF...');
    } else if (key.includes('excel')) {
      appToast.success('Exporting Holiday List to Excel...');
    }
  }, []);

  return {
    state: {
      records: paginatedRecords,
      allFilteredRecords: filteredRecords,
      totalCount,
      page,
      pageSize,
      totalPages,
      groupFilter,
      typeFilter,
      yearFilter,
      statusFilter,
      searchField,
      searchText,
      isLoading,
      groupOptions: GROUP_OPTIONS,
      typeOptions: HOLIDAY_TYPE_OPTIONS,
      yearOptions: YEAR_OPTIONS,
      statusOptions,
      searchFieldOptions,
      tools,
      summary,
    },
    actions: {
      setGroupFilter,
      setTypeFilter,
      setYearFilter,
      setStatusFilter,
      setSearchField,
      setSearchText,
      setPage,
      setPageSize,
      handleSearch,
      resetFilters,
      handleToolSelect,
    },
  };
}
